/**
 * @file       BlackWord vuex store, used by the whole blackWord module.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import axios from "@/plugins/axios.js";

/**
 * Gets the default state.
 *
 * @return  {Object}  The default state.
 */
function getDefaultState() {
  return {
    blackWords: {
      status: null,
      data: { data: [] },
      error: null,
      filters: {
        blackWordId: null,
        status: null,
        resourcesPerPage: 10,
        page: 1
      }
    },
    blackWord: {
      status: null,
      data: null,
      error: null
    }
  };
}

/**
 * BlackWord vuex actions.
 *
 * @type  {Object}
 */
const actions = {
  /**
   * Get black words.
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getBlackWords: ({ commit, state }) => {
    const payload = Object.entries(state.blackWords.filters).reduce(
      (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
      {}
    );
    return new Promise((resolve, reject) => {
      commit("onGetBlackWords");

      axios
        .get(`blackWord`, { params: payload })
        .then(response => {
          commit("onGetBlackWordsSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetBlackWordsError", error);
          reject(error);
        });
    });
  },

  /**
   * Update black word.
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    payload          The payload
   *
   * @return  {Promise}
   */
  putBlackWord: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onPutBlackWord");

      axios
        .put(`blackWord`, payload)
        .then(response => {
          commit("onPutBlackWordSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPutBlackWordError", error);
          reject(error);
        });
    });
  },

  /**
   * Add black word.
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    payload          The payload
   *
   * @return  {Promise}
   */
  postBlackWord: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onPostBlackWord");

      axios
        .post(`blackWord`, payload)
        .then(response => {
          commit("onPostBlackWordSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPostBlackWordError", error);
          reject(error);
        });
    });
  },

  /**
   * Patch a BlackWord.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  patchBlackWord: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onPatchBlackWord");
      axios
        .patch("blackWord", params)
        .then(response => {
          commit("onPatchBlackWordSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPatchBlackWordError", error);
          reject(error);
        });
    });
  }
};

/**
 * BlackWord vuex mutations.
 *
 * @type  {Object}
 */
const mutations = {
  /**
   * Commit black word.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetBlackWords: state => {
    state.blackWords.status = "fetching";
    state.blackWords.error = {};
  },

  /**
   * Commit black word success.
   *
   * @param  {Object}  state     The vuex state
   * @param  {Object}  arg2.data The data returned
   */
  onGetBlackWordsSuccess: (state, { data }) => {
    state.blackWords.status = "success";
    state.blackWords.error = null;
    state.blackWords.data = data;
  },

  /**
   * Commit black word error.
   *
   * @param  {Object}  state     The vuex state
   * @param  {Object}  arg2.data The data returned
   */
  onGetBlackWordsError: (state, err) => {
    state.blackWords.status = "error";
    state.blackWords.error = err;
  },

  /**
   * Commit black word filters.
   *
   * @param  {Object}  state     The vuex state
   * @param  {Object}  filters   The filters
   */
  setBlackWordsFilters: (state, filters) => {
    if (filters.reset) {
      state.blackWords.filters = {
        blackWordId: null,
        status: null,
        resourcesPerPage: 10,
        page: 1
      };
    }
    if (filters.fields) {
      state.blackWords.filters = {
        ...state.blackWords.filters,
        ...filters.fields
      };
    }
  },

  /**
   * Commit put black word.
   *
   * @param  {Object}  state   The vuex state
   */
  onPutBlackWord: state => {
    state.blackWord.status = "fetching";
    state.blackWord.error = {};
  },

  /**
   * Put black word success.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  arg1.data   The data
   */
  onPutBlackWordSuccess: (state, { data }) => {
    state.blackWord.status = "success";
    state.blackWord.error = null;
    state.blackWord.data = data;
  },

  /**
   * Put black word error.
   *
   * @param  {Object}  state    The vuex state
   * @param  {Object}  err      The error
   */
  onPutBlackWordError: (state, err) => {
    state.blackWord.status = "error";
    state.blackWord.error = err;
  },

  /**
   * Commit set black word.
   *
   * @param  {Object}  state   The vuex state
   */
  onPostBlackWord: state => {
    state.blackWord.status = "fetching";
    state.blackWord.error = {};
  },

  /**
   * Commit black word success.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  arg1.data   The data
   */
  onPostBlackWordSuccess: (state, { data }) => {
    state.blackWord.status = "success";
    state.blackWord.error = null;
    state.blackWord.data = data;
  },

  /**
   * Commit black word error.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  err    The error
   */
  onPostBlackWordError: (state, err) => {
    state.blackWord.status = "error";
    state.blackWord.error = err;
  },

  /**
   * Commit update black word.
   *
   * @param  {Object}  state   The vuex state
   */
  onPatchBlackWord: state => {
    state.blackWord.status = "fetching";
    state.blackWord.error = {};
  },

  /**
   * Commit update black word success.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  arg1.data   The data
   */
  onPatchBlackWordSuccess: (state, { data }) => {
    state.blackWord.status = "success";
    state.blackWord.error = null;
    state.blackWord.data = data;
  },

  /**
   * Commit update black word error.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  err    The error
   */
  onPatchBlackWordError: (state, err) => {
    state.blackWord.status = "error";
    state.blackWord.error = err;
  }
};

/**
 * Export the store.
 *
 * @type  {Object}
 */
export default {
  namespaced: true,
  state: getDefaultState(),
  actions,
  mutations
};
